/**
 * Correspondance code météo WMO (Open-Meteo) → libellé FR, icône et couleurs.
 * Les couleurs sont en hex (style inline) pour être indépendantes de la palette
 * Tailwind custom du projet : soleil jaune, pluie bleue, nuages gris, etc.
 */
import { IconProp } from "@fortawesome/fontawesome-svg-core";

type WeatherGroup =
  | "clear"
  | "partly"
  | "cloudy"
  | "fog"
  | "drizzle"
  | "rain"
  | "snow"
  | "showers"
  | "storm";

const CODE_GROUP: Record<number, WeatherGroup> = {
  0: "clear",
  1: "partly",
  2: "partly",
  3: "cloudy",
  45: "fog",
  48: "fog",
  51: "drizzle",
  53: "drizzle",
  55: "drizzle",
  56: "drizzle",
  57: "drizzle",
  61: "rain",
  63: "rain",
  65: "rain",
  66: "rain",
  67: "rain",
  71: "snow",
  73: "snow",
  75: "snow",
  77: "snow",
  80: "showers",
  81: "showers",
  82: "showers",
  85: "snow",
  86: "snow",
  95: "storm",
  96: "storm",
  99: "storm",
};

const LABELS: Record<WeatherGroup, string> = {
  clear: "Ciel dégagé",
  partly: "Partiellement nuageux",
  cloudy: "Couvert",
  fog: "Brouillard",
  drizzle: "Bruine",
  rain: "Pluie",
  snow: "Neige",
  showers: "Averses",
  storm: "Orage",
};

const ICONS: Record<WeatherGroup, { day: IconProp; night: IconProp }> = {
  clear: { day: "sun", night: "moon" },
  partly: { day: "cloud-sun", night: "cloud-moon" },
  cloudy: { day: "cloud", night: "cloud" },
  fog: { day: "smog", night: "smog" },
  drizzle: { day: "cloud-rain", night: "cloud-moon-rain" },
  rain: { day: "cloud-showers-heavy", night: "cloud-showers-heavy" },
  snow: { day: "snowflake", night: "snowflake" },
  showers: { day: "cloud-sun-rain", night: "cloud-moon-rain" },
  storm: { day: "cloud-bolt", night: "cloud-bolt" },
};

/** Couleur de l'icône (hex) par groupe. */
const COLORS: Record<WeatherGroup, string> = {
  clear: "#F5B301", // soleil jaune
  partly: "#F0B429", // jaune adouci
  cloudy: "#8A929B", // gris
  fog: "#A6ADB5", // gris clair
  drizzle: "#5BA9E0", // bleu
  rain: "#2E8BD6", // bleu plus soutenu
  snow: "#8FCDE9", // bleu glacé
  showers: "#4AA3DF", // bleu
  storm: "#F2912B", // orange/ambre éclair
};

const NIGHT_CLEAR_COLOR = "#7C84B8"; // lune indigo doux

export type WeatherDisplay = {
  label: string;
  icon: IconProp;
  /** Couleur de l'icône (hex). */
  color: string;
  /** Fond dégradé subtil (CSS) pour le bloc héro. */
  background: string;
};

/** Ajoute une transparence (~13%) à une couleur hex pour un fond léger. */
const tint = (hex: string) => `${hex}22`;

export function getWeatherDisplay(code: number, isDay = true): WeatherDisplay {
  const group = CODE_GROUP[code] ?? "cloudy";
  const icon = ICONS[group][isDay ? "day" : "night"];
  const color =
    !isDay && group === "clear" ? NIGHT_CLEAR_COLOR : COLORS[group];

  return {
    label: LABELS[group],
    icon,
    color,
    background: `linear-gradient(135deg, ${tint(color)} 0%, #FFFFFF 75%)`,
  };
}

/** Convertit un cap (degrés) en point cardinal (N, NE, E…). */
export function degreesToCardinal(deg: number): string {
  const points = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  return points[Math.round(deg / 45) % 8];
}

/**
 * Indice de stress thermique (THI) pour volailles à partir de la température (°C)
 * et de l'humidité relative (%). Formule usuelle aviaire.
 */
export function computeThi(tempC: number, humidity: number): number {
  return 0.8 * tempC + (humidity / 100) * (tempC - 14.4) + 46.4;
}

/** Niveau de stress thermique (couleur hex) à partir du THI. */
export function thiLevel(thi: number): { label: string; color: string } {
  if (thi < 70) return { label: "Normal", color: "#4CAF50" };
  if (thi < 80) return { label: "Alerte", color: "#FF9800" };
  return { label: "Danger", color: "#EF4444" };
}

/** Catégorie d'indice UV. */
export function uvLevel(uv: number): string {
  if (uv < 3) return "Faible";
  if (uv < 6) return "Modéré";
  if (uv < 8) return "Élevé";
  if (uv < 11) return "Très élevé";
  return "Extrême";
}
